import { useState } from "react";
import { ChevronDown, Users } from "lucide-react";
import { ClassroomResponseDto } from "@packages/api/madlenAPI.schemas";
import { useTranslation } from "react-i18next";
import { Button, Text } from "@core/index";
import { CourseData } from "@packages/constant/CourseData";

interface ClassSelectorProps {
  classes: ClassroomResponseDto[];
  selectedClass: ClassroomResponseDto | null;
  setSelectedClass: (classroomId: number) => void;
}

export const ClassSelector = ({
  classes,
  selectedClass,
  setSelectedClass,
}: ClassSelectorProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <Button
        fullWidth
        variant="outline"
        size="md"
        className="md:w-72 flex items-center justify-between dark:border-gray-700"
        onClick={() => setIsOpen(!isOpen)}
        rounded="lg"
        bgColor="bg-white dark:bg-gray-800"
      >
        <div>
          <Text variant="p" className="text-left" weight={"medium"}>
            {selectedClass?.name || "students.selectClass"}
          </Text>
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            {selectedClass?.grade && selectedClass.grade > 0
              ? selectedClass.grade + " •"
              : ""}
            {selectedClass && (
              <>
                <p>
                  {t(
                    "courseType." +
                      CourseData[selectedClass.courseType]?.name(
                        selectedClass.grade,
                      ),
                  )}
                </p>
                <span>•</span>
                <div className="flex items-center gap-1">
                  <Users size={14} />
                  <p>{selectedClass.studentCount || 0}</p>
                </div>
              </>
            )}
          </div>
        </div>
        <ChevronDown
          size={20}
          className={`text-gray-400 dark:text-gray-500 transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </Button>

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg shadow-lg max-h-60 overflow-auto">
          {classes.length === 0 && (
            <Text variant="p" status="secondary" className="text-center p-4">
              students.noClassrooms
            </Text>
          )}
          {classes.map((classroom) => (
            <Button
              key={classroom.id}
              className="block text-left focus:outline-none"
              fullWidth
              size="md"
              bgColor="hover:bg-gray-50 dark:hover:bg-gray-700 focus:bg-gray-50 dark:focus:bg-gray-700"
              onClick={() => {
                setSelectedClass(classroom.id);
                setIsOpen(false);
              }}
            >
              <Text variant="p" weight={"medium"}>
                {classroom.name}
              </Text>
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                {classroom.grade && classroom.grade > 0
                  ? classroom.grade + " •"
                  : ""}
                <p>
                  {t(
                    "courseType." +
                      CourseData[classroom.courseType]?.name(classroom.grade),
                  )}
                </p>
                <span>•</span>
                <div className="flex items-center gap-1">
                  <Users size={14} />
                  <p>{classroom.studentCount || 0}</p>
                </div>
              </div>
            </Button>
          ))}
        </div>
      )}
    </div>
  );
};
